import React, { useState } from 'react';
import { Table, Space, Typography, Spin, Checkbox, Switch, Select } from 'antd';
import type { TableColumnsType } from 'antd';
import { createStyles } from 'antd-style';
import {
  DownOutlined,
  UserOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  MoreOutlined,
  FilterOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { Campaign } from '../campaigns/demoData';
import { useNavigate } from 'react-router-dom';

const { Option } = Select;

const { Text } = Typography;

const useStyle = createStyles(({ css }) => {
  return {
    customTable: css`
      /* Custom scrollbar styles */
      .ant-table-container .ant-table-body,
      .ant-table-container .ant-table-content {
        scrollbar-width: thin;
        scrollbar-color: #eaeaea transparent;
        scrollbar-gutter: stable;
      }

      /* Increase row height and vertical alignment */
      .ant-table-tbody > tr > td {
        padding-top: 14px !important;
        padding-bottom: 14px !important;
        padding-left: 12px !important;
        padding-right: 12px !important;
        vertical-align: middle;
      }

      /* Fixed height for table body - shows more rows */
      .ant-table-container .ant-table-body {
        max-height: 600px;
        overflow-y: auto;
      }

      /* Ensure horizontal scrolling for wide tables */
      .ant-table-container {
        overflow-x: auto;
      }

      /* Ensure proper row height for consistent display */
      .ant-table-tbody > tr {
        height: auto;
        min-height: 50px;
      }
    `,
  };
});

interface CampaignInsightsTableProps {
  data: Campaign[];
  loading?: boolean;
  errorText?: string | null;
  total?: number;
  onPageChange?: (page: number, pageSize: number) => void;
  onRowClick?: (record: Campaign) => void;
  onToggleActive?: (
    record: Campaign,
    nextActive: boolean
  ) => void | Promise<void>;
  togglingId?: string | null;
}

const CampaignInsightsTable: React.FC<CampaignInsightsTableProps> = ({
  data,
  loading,
  errorText,
  total,
  onPageChange,
  onRowClick,
  onToggleActive,
  togglingId,
}) => {
  const { styles } = useStyle();
  const navigate = useNavigate();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Loading state - controlled by parent
  const isLoading = !!loading;
  const error = errorText;

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '50px', color: 'red' }}>
        Error loading campaign data: {error}
      </div>
    );
  }

  const getStatusBadge = (status: string) => {
    const isDelivering = status === 'Delivering';
    return (
      <div className="flex items-center space-x-2">
        <div
          className={`w-2 h-2 rounded-full ${isDelivering ? 'bg-green-500' : 'bg-gray-400'}`}
        ></div>
        <span className={isDelivering ? 'text-green-600' : 'text-gray-500'}>
          {status}
        </span>
      </div>
    );
  };

  const getTargetingTypeIcon = (type: string) => {
    return (
      <div className="flex items-center space-x-2">
        <UserOutlined
          className={type === 'Manual' ? 'text-blue-500' : 'text-orange-500'}
        />
        <span>{type}</span>
      </div>
    );
  };

  const getTrendIcon = (trend: 'up' | 'down') => {
    return trend === 'up' ? (
      <ArrowUpOutlined className="text-green-500 text-xs" />
    ) : (
      <ArrowDownOutlined className="text-red-500 text-xs" />
    );
  };

  const handleCampaignClick = (campaignId: string) => {
    // Navigate to Ad Groups page for the selected campaign.
    // Campaign settings data will now be fetched lazily only when that tab is opened.
    navigate(`/dashboard/ads/manage/adgroups?campaignId=${campaignId}`);
  };

  const columns: TableColumnsType<Campaign> = [
    {
      title: (
        <Checkbox
          checked={selectedRowKeys.length === data.length}
          indeterminate={
            selectedRowKeys.length > 0 && selectedRowKeys.length < data.length
          }
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRowKeys(data.map((item) => item?.id));
            } else {
              setSelectedRowKeys([]);
            }
          }}
        />
      ),
      dataIndex: 'select',
      key: 'select',
      width: 50,
      fixed: 'left',
      render: (_: any, record: Campaign) => (
        <Checkbox
          checked={selectedRowKeys.includes(record?.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRowKeys([...selectedRowKeys, record?.id]);
            } else {
              setSelectedRowKeys(
                selectedRowKeys.filter((key) => key !== record?.id)
              );
            }
          }}
        />
      ),
    },
    {
      title: (
        <Space>
          <Text>Active</Text>
          <DownOutlined className="text-xs" />
          <MoreOutlined className="text-xs" />
        </Space>
      ),
      dataIndex: 'isActive',
      key: 'isActive',
      width: 120,
      fixed: 'left',
      render: (isActive: boolean, record: Campaign) => (
        <Switch
          checked={isActive}
          size="small"
          loading={togglingId === record.id}
          disabled={togglingId === record.id}
          onChange={(checked) => {
            if (onToggleActive) {
              onToggleActive(record, checked);
            }
          }}
        />
      ),
      filterDropdown: () => (
        <div className="p-2">
          <Select defaultValue="all" style={{ width: 100 }}>
            <Option value="all">All</Option>
            <Option value="active">Active</Option>
            <Option value="inactive">Inactive</Option>
          </Select>
        </div>
      ),
      filterIcon: <FilterOutlined className="text-gray-400" />,
    },
    {
      title: (
        <Space>
          <Text>Campaigns</Text>
          <DownOutlined className="text-xs" />
          <MoreOutlined className="text-xs" />
        </Space>
      ),
      dataIndex: 'name',
      key: 'name',
      width: 200,
      fixed: 'left',
      render: (name: string, record: Campaign) => (
        <div className="flex items-center space-x-2">
          <CopyOutlined className="text-blue-500" />
          {onRowClick ? (
            // Portfolio mode: just show name, no link
            <span className="text-blue-600 font-medium">
              {name.toUpperCase()}
            </span>
          ) : (
            // Campaign mode: show clickable link
            <a
              className="text-blue-600 underline font-medium cursor-pointer hover:text-blue-800"
              onClick={(e) => {
                e.stopPropagation();
                handleCampaignClick(record.id);
              }}
            >
              {name.toUpperCase()}
            </a>
          )}
        </div>
      ),
      filterDropdown: () => (
        <div className="p-2">
          <Select defaultValue="all" style={{ width: 120 }}>
            <Option value="all">All Campaigns</Option>
          </Select>
        </div>
      ),
      filterIcon: <FilterOutlined className="text-gray-400" />,
    },
    {
      title: 'Campaign Attributes',
      children: [
        {
          title: 'Serving Status',
          dataIndex: 'servingStatus',
          key: '2',
          width: 150,
          render: (status: string) => getStatusBadge(status),
          sorter: (a, b) => a.servingStatus.length - b.servingStatus.length,
        },
        {
          title: 'Targeting Type',
          dataIndex: 'targetingType',
          key: '3',
          width: 150,
          render: (type: string) => getTargetingTypeIcon(type),
          sorter: (a, b) => a.targetingType.length - b.targetingType.length,
        },
        {
          title: 'Daily Budget',
          dataIndex: 'dailyBudget',
          key: '4',
          width: 120,
          render: (budget: string) => (
            <span className="font-medium">{budget}</span>
          ),
          sorter: (a, b) => {
            const aVal = parseFloat(a.dailyBudget.replace(/[₹,K]/g, ''));
            const bVal = parseFloat(b.dailyBudget.replace(/[₹,K]/g, ''));
            return aVal - bVal;
          },
        },
        {
          title: 'Budget Utilization',
          dataIndex: 'budgetUtilization',
          key: '5',
          width: 150,
          render: (utilization: string) => (
            <span className="text-gray-600">{utilization}</span>
          ),
          sorter: (a, b) => {
            const aVal = parseFloat(a.budgetUtilization.replace('%', ''));
            const bVal = parseFloat(b.budgetUtilization.replace('%', ''));
            return aVal - bVal;
          },
        },
      ],
    },
    {
      title: 'Performance Metrics',
      children: [
        {
          title: 'Impressions',
          dataIndex: 'impressions',
          key: '1',
          width: 150,
          render: (impressions: string, record: Campaign) => (
            <div className="flex items-center space-x-1">
              <span className="border-b border-dotted border-gray-400">
                {impressions}
              </span>
              {getTrendIcon(record.impressionsTrend)}
            </div>
          ),
          sorter: (a, b) => {
            const aVal = parseFloat(a.impressions.replace(/[K,]/g, ''));
            const bVal = parseFloat(b.impressions.replace(/[K,]/g, ''));
            return aVal - bVal;
          },
        },
        {
          title: 'Clicks',
          dataIndex: 'clicks',
          key: '2',
          width: 150,
          render: (clicks: string, record: Campaign) => (
            <div className="flex items-center space-x-1">
              <span className="border-b border-dotted border-gray-400">
                {clicks}
              </span>
              {getTrendIcon(record.clicksTrend)}
            </div>
          ),
          sorter: (a, b) => {
            const aVal = parseFloat(a.clicks.replace(/[K,]/g, ''));
            const bVal = parseFloat(b.clicks.replace(/[K,]/g, ''));
            return aVal - bVal;
          },
        },
        {
          title: 'Sales',
          dataIndex: 'sales',
          key: '3',
          width: 150,
          render: (sales: string, record: Campaign) => (
            <div className="flex items-center space-x-1">
              <span className="border-b border-dotted border-gray-400">
                {sales}
              </span>
              {getTrendIcon(record.salesTrend)}
            </div>
          ),
          sorter: (a, b) => {
            const aVal = parseFloat(a.sales.replace(/[₹,K]/g, ''));
            const bVal = parseFloat(b.sales.replace(/[₹,K]/g, ''));
            return aVal - bVal;
          },
        },
      ],
    },
    {
      title: 'Derived Metrics',
      children: [
        {
          title: 'ACoS',
          dataIndex: 'acos',
          key: '1',
          width: 150,
          render: (acos: string, record: Campaign) => (
            <div className="flex items-center space-x-1">
              <span className="border-b border-dotted border-gray-400">
                {acos}
              </span>
              {getTrendIcon(record.acosTrend)}
            </div>
          ),
          sorter: (a, b) => {
            const aVal = parseFloat(a.acos.replace('%', ''));
            const bVal = parseFloat(b.acos.replace('%', ''));
            return aVal - bVal;
          },
        },
        {
          title: 'ROAS',
          dataIndex: 'roas',
          key: '2',
          width: 150,
          render: (roas: string, record: Campaign) => (
            <div className="flex items-center space-x-1">
              <span className="border-b border-dotted border-gray-400">
                {roas}
              </span>
              {getTrendIcon(record.roasTrend)}
            </div>
          ),
          sorter: (a, b) => {
            const aVal = parseFloat(a.roas);
            const bVal = parseFloat(b.roas);
            return aVal - bVal;
          },
        },
        {
          title: 'CVR',
          dataIndex: 'cvr',
          key: '3',
          width: 150,
          render: (cvr: string, record: Campaign) => (
            <div className="flex items-center space-x-1">
              <span className="border-b border-dotted border-gray-400">
                {cvr}
              </span>
              {getTrendIcon(record.cvrTrend)}
            </div>
          ),
          sorter: (a, b) => {
            const aVal = parseFloat(a.cvr.replace('%', ''));
            const bVal = parseFloat(b.cvr.replace('%', ''));
            return aVal - bVal;
          },
        },
      ],
    },
  ];

  return (
    <div className="space-y-4">
      {/* Data Table */}
      <Table<Campaign>
        className={styles.customTable}
        columns={columns}
        dataSource={data}
        rowKey="id"
        bordered={true}
        scroll={{ x: 'max-content', y: 600 }}
        onRow={(record) => ({
          onClick: (e) => {
            // Prevent navigation if clicking on links/buttons inside the row
            const target = e.target as HTMLElement;
            if (target.closest('a, button, input, .ant-switch')) {
              return;
            }
            if (onRowClick) {
              onRowClick(record);
            }
          },
          style: onRowClick ? { cursor: 'pointer' } : {},
        })}
        pagination={{
          total: total ?? data.length,
          pageSize: pageSize,
          current: currentPage,
          onChange: (page, size) => {
            setCurrentPage(page);
            if (size !== pageSize) {
              setPageSize(size);
            }
            onPageChange?.(page, size ?? pageSize);
          },
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
        }}
      />
    </div>
  );
};

export default CampaignInsightsTable;
